import http from 'src/infrastructure/http'
import { parseFailedResponse, parseGoodResponse } from 'src/infrastructure/parse-axios-response'
import {
  GetSwapsOrReturnsPayload,
  GenericApiErrors,
  GetDataForRequestCreationPayload,
  SwapOrReturnData,
  CreateOrUpdateRequestPayload,
  RequestTagInfo,
  SwapOrReturnToResolveData,
  ValidateSwapOrReturnToResolvePayload,
  CountBatchesToReturnPayload,
  SwapAddOrRemoveReceiveBatchesPayload,
  SwapAddOrRemoveReceiveBatchesResponseData,
  ValidateRequestsToResolvePayload,
  FinishSwapOrReturnToResolvePayload,
  ValidateSupplierReleasePayload,
  ReleaseReturnRequestPayload,
  SwapOrReturnInvoiceData,
} from './SwapOrReturnTypes'
import { camelToSnakeCase } from 'src/utils/formatString'

const getSwapsOrReturns = async (payload: GetSwapsOrReturnsPayload) => {
  try {
    const {
      fulfillmentCenterId, supplierName, invoice, typesIds, status, page, perPage, order, orderBy,
    } = payload

    const convertedOrderBy = orderBy ? camelToSnakeCase(orderBy.toString()) : undefined

    const result = await http.post(`/v2/swap-and-return/search?page=${page}&per_page=${perPage}`, {
      id_fulfillment_center: fulfillmentCenterId,
      supplier_name: supplierName,
      invoice,
      ids_types: typesIds,
      status,
      order,
      order_by: convertedOrderBy,
    })

    return parseGoodResponse<SwapOrReturnInvoiceData>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const cancelSwapOrReturn = async (id: number) => {
  try {
    const result = await http.put(`/v2/swap-and-return/cancel/${id}`)

    return parseGoodResponse<never>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getDataForRequestCreation = async (payload: GetDataForRequestCreationPayload) => {
  try {
    const { invoiceKey } = payload
    const result = await http.get('/v2/swap-and-return/request/create/data', {
      params: {
        invoice_key: invoiceKey,
      },
    })
    return parseGoodResponse<SwapOrReturnData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getRequestProductsToEdit = async (idRequest: number) => {
  try {
    const result = await http.get('/v2/swap-and-return/request/products/edit', {
      params: {
        id_request: idRequest,
      },
    })
    return parseGoodResponse<SwapOrReturnData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const createOrUpdateRequest = async (payload: CreateOrUpdateRequestPayload) => {
  const {
    invoiceKey, idRequest, requestTypeId, products,
  } = payload
  try {
    const produtsTransfomed = products.map((product) => {
      const filteredBatches = product.batches.filter((batch) => batch.inputQuantity && batch.inputQuantity > 0)

      const batches = filteredBatches.map((batch) => ({
        id_batch: batch.idBatch,
        quantity: batch.quantity,
        input_quantity: batch.inputQuantity,
        expiration_date: batch.expirationDate,
      }))

      return {
        id_model: product.idModel,
        product_code: product.productCode,
        description: product.description,
        total_invoice_quantity: product.totalInvoiceQuantity,
        available_pending_quantity: product.availablePendingQuantity,
        quantity_per_box: product.quantityPerBox,
        input_quantity: product.inputQuantity,
        measure_unit: product.measureUnit,
        batches,
      }
    })

    const result = await http.post('/v2/swap-and-return/request/create', {
      id_request: idRequest || null,
      invoice_key: invoiceKey || null,
      id_request_type: requestTypeId,
      products: produtsTransfomed,
    })
    return parseGoodResponse<RequestTagInfo[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getSwapOrReturnToResolve = async (id: number) => {
  try {
    const result = await http.get(`/v2/swap-and-return/resolve/${id}`)

    return parseGoodResponse<SwapOrReturnToResolveData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const startSwapOrReturnToResolve = async (id: number, idFulfillmentCenter: number) => {
  try {
    const result = await http.put(`/v2/swap-and-return/resolve/start/${id}`, {
      id_fulfillment_center: idFulfillmentCenter,
    })

    return parseGoodResponse<any>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const delaySwapOrReturnToResolve = async (id: number) => {
  try {
    const result = await http.put(`/v2/swap-and-return/resolve/delay/${id}`)

    return parseGoodResponse<any>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateSwapOrReturnToResolve = async (payload: ValidateSwapOrReturnToResolvePayload) => {
  const { itemId, productBarcode, registerBarcode } = payload

  try {
    const result = await http.post('/v2/swap-and-return/resolve/validate-item', {
      id_item: itemId,
      product_barcode: productBarcode,
      register_barcode: registerBarcode,
    })

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const countBatchesToReturn = async (payload: CountBatchesToReturnPayload) => {
  try {
    const batches = payload.batches.map((batch) => ({
      id_request_item_batch: batch.idRequestItemBatch,
      quantity: batch.quantity,
      expiration_batches: batch.expirationBatches
        .filter((expirationBatch) => expirationBatch.countedQuantity !== undefined && expirationBatch.countedQuantity > 0)
        .map((expirationBatch) => ({
          id_expiration_batch: expirationBatch.idExpirationBatch,
          available_quantity: expirationBatch.availableQuantity,
          counted_quantity: expirationBatch.countedQuantity,
          id_location: expirationBatch.idLocation,
          address: expirationBatch.address,
          id_location_type: expirationBatch.idLocationType,
        })),
    }))

    const result = await http.post('/v2/swap-and-return/resolve/count/return', {
      id_item: payload.idItem,
      batches,
    })

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const swapAddOrRemoveReceiveBatches = async (payload: SwapAddOrRemoveReceiveBatchesPayload) => {
  try {
    const {
      idItem, idModel, barcode, receiveBatches,
    } = payload

    const batches = receiveBatches.map((batch) => ({
      due_date: batch.dueDate,
      quantity: batch.quantity,
      remove: batch.remove || false,
      id_batch: batch.idBatch || null,
    }))

    const result = await http.post('/v2/swap-and-return/resolve/count/receive/add-or-remove-batches', {
      id_item: idItem,
      id_model: idModel,
      barcode,
      receive_batches: batches,
    })

    return parseGoodResponse<SwapAddOrRemoveReceiveBatchesResponseData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const undoItemCount = async (id: number) => {
  try {
    const result = await http.put(`/v2/swap-and-return/resolve/count/undo/${id}`)

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishSwapOrReturnToResolve = async (payload: FinishSwapOrReturnToResolvePayload) => {
  try {
    const result = await http.post('/v2/swap-and-return/resolve/finish', {
      id_request: payload.idRequest,
      ids_requests: payload.idsRequests,
    })

    return parseGoodResponse<any>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const swapOrReturnToResolveItemNotFound = async (id: number) => {
  try {
    const result = await http.put(`/v2/swap-and-return/resolve/item-not-found/${id}`)

    return parseGoodResponse<any>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateRequestsToResolve = async (payload: ValidateRequestsToResolvePayload) => {
  try {
    const result = await http.post('/v2/swap-and-return/resolve/validate-requests', {
      ids_requests: payload.idsRequests,
    })

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateSupplierRelease = async (payload: ValidateSupplierReleasePayload) => {
  try {
    const result = await http.post('/v2/swap-and-return/resolve/validate-supplier-release', {
      id_current_request: payload.idCurrentRequest,
      ids_selected_requests: payload.idsSelectedRequests,
    })

    return parseGoodResponse<any>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const releaseReturnRequest = async (payload: ReleaseReturnRequestPayload) => {
  try {
    const result = await http.post(`/v2/swap-and-return/${payload.idRequest}/release`)

    return parseGoodResponse<never>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getSwapsOrReturns,
  cancelSwapOrReturn,
  getDataForRequestCreation,
  getRequestProductsToEdit,
  createOrUpdateRequest,
  getSwapOrReturnToResolve,
  startSwapOrReturnToResolve,
  delaySwapOrReturnToResolve,
  validateSwapOrReturnToResolve,
  countBatchesToReturn,
  swapAddOrRemoveReceiveBatches,
  undoItemCount,
  finishSwapOrReturnToResolve,
  swapOrReturnToResolveItemNotFound,
  validateRequestsToResolve,
  validateSupplierRelease,
  releaseReturnRequest,
}
